/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';

const auditParams = {
  include_audit: true,
  verbose: true
};

const customDataTypesEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/data_types`
    )
  );

const customDataTypeEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  dataTypeId: string
): string =>
  urlJoin(
    customDataTypesEndpoint(skillId, useNewIAUpdatedAccess, assistantId),
    dataTypeId
  );

const systemDataTypesEndpoint = () => {
  return urlJoin(endpoints.store, 'v2', 'system_data_types');
};

const validateAll = skillId => {
  const validationErrors = [];
  if (skillId) {
    const skillErrors = validation.validateGuid(skillId);
    if (skillErrors) {
      validationErrors.push(skillErrors);
    }
  }

  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }

  return null;
};

export const listCustomDataTypes = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  params: any = {}
) => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  return axios
    .get(customDataTypesEndpoint(skillId, useNewIAUpdatedAccess, assistantId), {
      params: {
        ...auditParams,
        ...params
      }
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });
};
export const listNextPageCustomDataTypes = nextUrl => {
  return axios
    .get(urlJoin(endpoints.store, nextUrl))
    .then(response => response.data)
    .catch(error => {
      throw error;
    });
};

export const listSystemDataTypes = (params = {}) => {
  return axios
    .get(systemDataTypesEndpoint(), {
      params: {
        ...auditParams,
        ...params
      }
    })
    .then(response => response.data)
    .catch(error => {
      throw error;
    });
};

export const fetchCustomDataType = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  dataTypeId: string,
  params: any = {}
) => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  try {
    const response = await axios.get(
      customDataTypeEndpoint(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        dataTypeId
      ),
      {
        params: {
          ...auditParams,
          ...params,
          include_references: true
        }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
